import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import { routeList } from '@/router/routes'
import { useAuthGuard } from '@/router/guards/authGuard'
import { useUIStore } from '@/stores/uiStore'
import { useAPI } from '@/lib/utils/useAPI'

/**
 * Vue Router instance. Routes registered DYNAMICALLY via `addRoute`
 * — keeps the construction minimal, lets routes split across files
 * (guest vs auth) without a giant inline array.
 *
 * Single global guard handles auth + role/permission gating; route-
 * level `beforeEnter` is reserved for special cases (Logout).
 *
 * Navigation-splash wiring: a dedicated `beforeEach` flips the UI
 * store's `navigating` latch ON; `afterEach` + `onError` flip it
 * OFF. The splash component (mounted globally in `App.vue`) gates
 * itself on a short debounce so fast navs don't flash. Splash
 * guards registered BEFORE the auth guard so the latch is on for
 * the duration of auth resolution too (which is usually instant
 * after the first nav, but still — correct ordering).
 */
const router = createRouter({
  history: createWebHistory(),
  routes: [],
})

routeList(router).forEach((route) => router.addRoute(route))

/**
 * Cancel every in-flight HTTP request on real route navigation.
 * Stale data + stale retries from the page-being-left shouldn't
 * keep firing in the background. Wakes any retry-backoff sleeps
 * (the sleep helper participates in AbortSignal cancellation).
 *
 *   - Skips the boot-time first nav (`from === START_LOCATION`) —
 *     nothing in flight yet, and globalStore's boot probe must
 *     not be aborted by its own initial route resolve.
 *   - Skips QUERY-ONLY changes (same `path`, different query) —
 *     these are in-page state, NOT navigating away: the same view
 *     stays mounted (e.g. Configs `?section=` deep-link tabs, a
 *     list's `?page=` / filters). A tab switch updates the query
 *     AND re-mounts the section, which fetches in its own
 *     `onMounted`; cancelling here aborts that very request — the
 *     "Integrations renders once, blank on re-nav" bug. The
 *     section's fetch isn't stale; it's the new page's data.
 *   - Still cancels same-name navs with different PARAMS (e.g. one
 *     kiosk detail → another: `/kiosks/5` → `/kiosks/6`) — params
 *     live in `path`, so those are real navigations whose old
 *     fetches ARE stale; new ones start after this hook returns.
 *
 * `reason: 'route-change'` is NOT 'auth-lockout' so it does NOT
 * trip the lockout latch — subsequent requests proceed normally
 * (see `BackendApi.cancelAllInFlight`).
 */
router.beforeEach((to, from) => {
  if (from === START_LOCATION) return
  if (to.path === from.path) return
  useAPI().cancelAllInFlight('route-change')
})
router.beforeEach(() => {
  useUIStore().startNavigation()
})
router.beforeEach(useAuthGuard(router))
router.afterEach(() => {
  useUIStore().finishNavigation()
})
router.onError(() => {
  useUIStore().finishNavigation()
})

export default router
